// exclude-from-index
import React, { Component } from 'react';
import { noop, debounce } from 'lodash';
import PropTypes from 'prop-types';
import deburr from 'lodash/deburr';
import Downshift from 'downshift';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import { ThemeProvider, StyledEngineProvider, createTheme } from '@mui/material/styles';
import withStyles from '@mui/styles/withStyles';
import { muiGlobalTheme } from '@themes';
import { colors } from '../../css';
import { searchSuggestions } from '../../css/zindex';
import { renderSuggestion } from './Suggestions';
import { WithStylesClassesPropType } from '../../../lib/commonPropTypes';

const styles = (theme) => ({
  root: {
    flexGrow: 1
  },
  container: {
    flexGrow: 1,
    position: 'relative'
  },
  paper: {
    position: 'absolute',
    zIndex: searchSuggestions,
    marginTop: theme.spacing(1),
    left: 0,
    right: 0
  },
  inputRoot: {
    flexWrap: 'wrap'
  },
  inputInput: {
    width: 'auto',
    flexGrow: 1
  }
});

function Input({ InputProps, classes: { inputRoot, inputInput }, ref, ...other }) {
  const classes = { root: inputRoot, input: inputInput };
  const { onChange, ...otherInputProps } = InputProps;
  const props = {
    InputProps: {
      inputRef: ref,
      classes,
      ...otherInputProps,
      onChange,
      autoComplete: 'off'
    },
    ...other
  };
  return <TextField {...props} />;
}
Input.propTypes = {
  InputProps: PropTypes.shape({ onChange: PropTypes.func }).isRequired,
  classes: PropTypes.shape({ inputRoot: PropTypes.shape({}), inputInput: PropTypes.shape({}) }).isRequired,

  ref: PropTypes.shape({}).isRequired
};

class AutoComplete extends Component {
  constructor(props) {
    super(props);
    const { onInputChange, debounceTime } = props;
    muiGlobalTheme.palette.text.primary = '#000';
    muiGlobalTheme.palette.text.secondary = colors.colorGrey03;
    muiGlobalTheme.components.MuiIconButton = {
      styleOverrides: { root: { marginBottom: '10px' } }
    };
    this.inputTheme = createTheme(muiGlobalTheme);
    this.debounceEmit = debounce((value) => onInputChange(value), debounceTime);
  }

  getSuggestions = (value) => {
    const { suggestions, suggestionCount } = this.props;
    const inputValue = deburr(value.trim()).toLowerCase();
    let count = 0;
    return suggestions.filter(({ label }) => {
      const keep = count < suggestionCount && label.slice(0, inputValue.length).toLowerCase() === inputValue;
      count += keep;
      return keep;
    });
  };

  render() {
    const { classes, theme, errorText, helperText, suggestions, openOnFocus, ...defaultProps } = this.props;
    const { error, ...InputProps } = defaultProps;
    const { value, onChange, label, required, disabled, placeholder } = InputProps;
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={this.inputTheme}>
          <div className={classes.root}>
            <Downshift
              id="downshift-simple"
              selectedItem={value}
              onChange={onChange}
              onInputValueChange={this.debounceEmit}
              itemToString={(item) => item?.label ?? ''}
            >
              {({
                getInputProps,
                getItemProps,
                getMenuProps,
                openMenu,
                highlightedIndex,
                inputValue,
                isOpen,
                selectedItem
              }) => (
                <div className={classes.container}>
                  <Input
                    fullWidth
                    classes={classes}
                    InputProps={getInputProps(openOnFocus ? { onFocus: openMenu } : {})}
                    error={error}
                    helperText={error ? errorText : helperText}
                    label={label}
                    required={required}
                    disabled={disabled}
                    placeholder={placeholder}
                  />
                  <div {...getMenuProps()}>
                    {isOpen && (
                      <Paper elevation={2} className={classes.paper} square>
                        {this.getSuggestions(inputValue).map((suggestion, index) =>
                          renderSuggestion({
                            suggestion,
                            index,
                            itemProps: getItemProps({ item: suggestion }),
                            highlightedIndex,
                            selectedItem,
                            inputValue
                          })
                        )}
                      </Paper>
                    )}
                  </div>
                </div>
              )}
            </Downshift>
          </div>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }
}

AutoComplete.propTypes = {
  // Message that is displayed when the input has an error
  errorText: PropTypes.string,
  // Optional helper text that sits below the input
  helperText: PropTypes.string,
  // the suggestions for autocomplete
  suggestions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) })
  ),
  openOnFocus: PropTypes.bool,
  classes: WithStylesClassesPropType.isRequired,
  theme: PropTypes.shape({}).isRequired,
  onInputChange: PropTypes.func,
  suggestionCount: PropTypes.number,
  debounceTime: PropTypes.number
};

AutoComplete.defaultProps = {
  errorText: 'Error',
  openOnFocus: false,
  helperText: undefined,
  suggestions: [],
  suggestionCount: 5,
  onInputChange: noop,
  debounceTime: 300
};

const StyledAutoComplete = withStyles(styles, { withTheme: true })(AutoComplete);

export default StyledAutoComplete;
